class MemoryCache {
    constructor(defaultTTL = 5 * 60 * 1000) {
        this.cache = new Map();
        this.defaultTTL = defaultTTL;
    }

    set(key, value, ttl = this.defaultTTL) {
        const expiry = Date.now() + ttl;
        this.cache.set(key, { value, expiry });
        return value;
    }

    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;

        if (Date.now() > item.expiry) {
            this.cache.delete(key);
            return null;
        }

        return item.value;
    }

    has(key) {
        const item = this.cache.get(key);
        if (!item) return false;

        if (Date.now() > item.expiry) {
            this.cache.delete(key);
            return false;
        }

        return true;
    }

    delete(key) {
        return this.cache.delete(key);
    }

    clear() {
        this.cache.clear();
    }

    cleanup() {
        const now = Date.now();
        for (const [key, item] of this.cache.entries()) {
            if (now > item.expiry) {
                this.cache.delete(key);
            }
        }
    }

    size() {
        return this.cache.size;
    }
}

const globalCache = new MemoryCache();

setInterval(() => {
    globalCache.cleanup();
}, 5 * 60 * 1000);

class RequestBatcher {
    constructor(batchSize = 10, delay = 100) {
        this.batchSize = batchSize;
        this.delay = delay;
        this.queue = [];
        this.processing = false;
    }

    add(request) {
        return new Promise((resolve, reject) => {
            this.queue.push({ request, resolve, reject });
            this.processBatch();
        });
    }

    async processBatch() {
        if (this.processing || this.queue.length === 0) return;

        this.processing = true;
        await new Promise(resolve => setTimeout(resolve, this.delay));

        const batch = this.queue.splice(0, this.batchSize);
        
        try {
            const requests = batch.map(item => item.request());
            const results = await Promise.allSettled(requests);

            results.forEach((result, index) => {
                const { resolve, reject } = batch[index];
                if (result.status === 'fulfilled') {
                    resolve(result.value);
                } else {
                    reject(result.reason);
                }
            });
        } catch (error) {
            batch.forEach(({ reject }) => reject(error));
        }

        this.processing = false;
        
        if (this.queue.length > 0) {
            this.processBatch();
        }
    }
}

class PerformanceMonitor {
    constructor() {
        this.marks = new Map();
        this.measures = new Map();
    }

    mark(name) {
        const timestamp = performance.now();
        this.marks.set(name, timestamp);
        if (typeof performance.mark === 'function') {
            performance.mark(name);
        }
        return timestamp;
    }

    measure(name, startMark, endMark) {
        const startTime = this.marks.get(startMark);
        const endTime = endMark ? this.marks.get(endMark) : performance.now();
        
        if (startTime === undefined) {
            console.warn(`Start mark "${startMark}" not found`);
            return null;
        }

        const duration = endTime - startTime;
        this.measures.set(name, duration);
        
        if (typeof performance.measure === 'function') {
            try {
                performance.measure(name, startMark, endMark);
            } catch (e) {
                console.warn('Performance.measure failed:', e);
            }
        }
        
        return duration;
    }

    getNavigationTiming() {
        if (!performance.navigation || !performance.timing) {
            return null;
        }

        const timing = performance.timing;
        return {
            dns: timing.domainLookupEnd - timing.domainLookupStart,
            tcp: timing.connectEnd - timing.connectStart,
            request: timing.responseStart - timing.requestStart,
            response: timing.responseEnd - timing.responseStart,
            dom: timing.domContentLoadedEventEnd - timing.navigationStart,
            load: timing.loadEventEnd - timing.navigationStart
        };
    }

    logMetrics() {
        const navigation = this.getNavigationTiming();
        if (navigation) {
            console.group('Performance Metrics');
            console.log('DNS Lookup:', navigation.dns + 'ms');
            console.log('TCP Connection:', navigation.tcp + 'ms');
            console.log('Request Time:', navigation.request + 'ms');
            console.log('Response Time:', navigation.response + 'ms');
            console.log('DOM Ready:', navigation.dom + 'ms');
            console.log('Page Load:', navigation.load + 'ms');
            console.groupEnd();
        }

        if (this.measures.size > 0) {
            console.group('Custom Measures');
            for (const [name, duration] of this.measures) {
                console.log(`${name}:`, duration + 'ms');
            }
            console.groupEnd();
        }
    }

    clear() {
        this.marks.clear();
        this.measures.clear();
        if (typeof performance.clearMarks === 'function') {
            performance.clearMarks();
        }
        if (typeof performance.clearMeasures === 'function') {
            performance.clearMeasures();
        }
    }
}

const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
};

const throttle = (func, limit) => {
    let inThrottle;
    return (...args) => {
        if (!inThrottle) {
            func.apply(null, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
};

const memoize = (func, keyGenerator = (...args) => JSON.stringify(args)) => {
    const cache = new Map();
    return (...args) => {
        const key = keyGenerator(...args);
        if (cache.has(key)) {
            return cache.get(key);
        }
        const result = func(...args);
        cache.set(key, result);
        return result;
    };
};

const lazyLoad = (importFunc) => {
    let modulePromise;
    return () => {
        if (!modulePromise) {
            modulePromise = importFunc();
        }
        return modulePromise;
    };
};

export {
    MemoryCache,
    RequestBatcher,
    PerformanceMonitor,
    globalCache,
    debounce,
    throttle,
    memoize,
    lazyLoad
};